import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger } from '../../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { toast } from 'sonner';
import { UserPlus, Pencil, Trash2, Eye, LogOut, Users, Send, FileText, Clock, Shield, TrendingUp, Activity, Copy, RefreshCw } from 'lucide-react';
import SwitchViewMenu from '../../components/SwitchViewMenu';
import { ResendInviteButton } from '../../components/ResendInviteButton';
import { AdvisorNotifications } from '../../components/AdvisorNotifications';
import { useAuth, formatApiErrorDetail } from '../../context/AuthContext';
import FormPreview from './FormPreview';
import AnalyticsPanel from './AnalyticsPanel';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

export default function AdminDashboard() {
  const formatLastActive = (iso) => {
    if (!iso) return <span className="text-[#94a3b8]">Never</span>;
    const then = new Date(iso);
    const diffMs = Date.now() - then.getTime();
    const mins = Math.floor(diffMs / 60000);
    if (mins < 1) return <span className="text-green-700 font-medium">Just now</span>;
    if (mins < 60) return <span className="text-green-700">{mins}m ago</span>;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return <span className="text-green-700">{hrs}h ago</span>;
    const days = Math.floor(hrs / 24);
    if (days < 7) return <span>{days}d ago</span>;
    if (days < 30) return <span className="text-[#475569]">{Math.floor(days / 7)}w ago</span>;
    if (days < 90) return <span className="text-amber-700">{Math.floor(days / 30)}mo ago</span>;
    return <span className="text-red-700 font-medium" title={then.toLocaleString()}>{then.toLocaleDateString()}</span>;
  };

  const [data, setData] = useState({ stats: {}, advisors: [], invites: [], submissions: [] });
  const [analytics, setAnalytics] = useState(null);
  const [auditLogs, setAuditLogs] = useState([]);
  const [allowlist, setAllowlist] = useState([]);
  const [newAllowEmail, setNewAllowEmail] = useState('');
  const [newAllowRole, setNewAllowRole] = useState('advisor');
  const [runningReminders, setRunningReminders] = useState(false);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('advisors');
  const [createAdvisorOpen, setCreateAdvisorOpen] = useState(false);
  const [newAdvisor, setNewAdvisor] = useState({ username: '', password: '', name: '', email: '', phone: '' });
  const [newCSA, setNewCSA] = useState({ password: '', name: '', email: '', phone: '' });
  const [createCSAOpen, setCreateCSAOpen] = useState(false);
  const [editAdvisorOpen, setEditAdvisorOpen] = useState(false);
  const [editAdvisor, setEditAdvisor] = useState({ id: '', name: '', email: '', phone: '', password: '' });
  // Invite link dialog state
  const [linkDialogOpen, setLinkDialogOpen] = useState(false);
  const [selectedInvite, setSelectedInvite] = useState(null);
  const [retrievedLink, setRetrievedLink] = useState('');
  const [retrieving, setRetrieving] = useState(false);
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const loadDashboard = useCallback(async () => {
    try {
      const [dashRes, analyticsRes, logsRes, allowRes] = await Promise.all([
        axios.get(`${API}/admin/dashboard`, { withCredentials: true }),
        axios.get(`${API}/admin/analytics`, { withCredentials: true }),
        axios.get(`${API}/admin/audit-logs`, { withCredentials: true }),
        axios.get(`${API}/admin/allowlist`, { withCredentials: true }),
      ]);
      setData(dashRes.data);
      setAnalytics(analyticsRes.data);
      setAuditLogs(logsRes.data);
      setAllowlist(allowRes.data || []);
    } catch (e) {
      toast.error('Failed to load admin dashboard');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadDashboard();
  }, [loadDashboard]);

  const handleAddAllowlist = async () => {
    const email = (newAllowEmail || '').trim().toLowerCase();
    if (!email) { toast.error('Enter an email'); return; }
    try {
      const { data } = await axios.post(`${API}/admin/allowlist`, { email, role: newAllowRole }, { withCredentials: true });
      if (data.email_sent) {
        toast.success(`${email} added and invitation email sent`);
      } else {
        const reason = data.email_error
          ? `Email delivery failed: ${data.email_error}`
          : 'Email delivery is not configured.';
        try { if (data.signup_url) await navigator.clipboard.writeText(data.signup_url); } catch (err) { console.warn("suppressed error:", err); }
        toast.warning(`${email} added to allowlist. ${reason} Signup link copied to clipboard — share it manually.`, { duration: 12000 });
      }
      setNewAllowEmail('');
      loadDashboard();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const handleRemoveAllowlist = async (email) => {
    if (!window.confirm(`Remove ${email} from the signup allowlist?`)) return;
    try {
      await axios.delete(`${API}/admin/allowlist/${encodeURIComponent(email)}`, { withCredentials: true });
      toast.success('Removed from allowlist');
      loadDashboard();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const handleRunReminders = async () => {
    setRunningReminders(true);
    try {
      await axios.post(`${API}/admin/reminders/run-now`, {}, { withCredentials: true });
      toast.success('Reminder scan complete — any due reminders were dispatched');
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setRunningReminders(false);
    }
  };


  const openEditDialog = (adv) => {
    setEditAdvisor({ id: adv.id, name: adv.name || '', email: adv.email || '', phone: adv.phone || '', password: '' });
    setEditAdvisorOpen(true);
  };

  const handleUpdateAdvisor = async () => {
    try {
      const payload = { name: editAdvisor.name, email: editAdvisor.email, phone: editAdvisor.phone };
      if (editAdvisor.password) payload.password = editAdvisor.password;
      await axios.patch(`${API}/admin/advisors/${editAdvisor.id}`, payload, { withCredentials: true });
      toast.success('Advisor updated');
      setEditAdvisorOpen(false);
      loadDashboard();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const handleCreateCSA = async () => {
    if (!newCSA.name || !newCSA.email) {
      toast.error('Name and email are required');
      return;
    }
    if (newCSA.password && newCSA.password.length < 8) {
      toast.error('Password must be at least 8 characters (or leave blank to send a setup link)');
      return;
    }
    try {
      const payload = { ...newCSA, role: 'csa' };
      if (!payload.password) delete payload.password;
      const { data } = await axios.post(`${API}/admin/advisors`, payload, { withCredentials: true });

      if (data.setup_url) {
        if (data.email_sent) {
          toast.success(`CSA "${newCSA.name}" invited — setup link emailed to ${newCSA.email}`);
        } else {
          try { await navigator.clipboard.writeText(data.setup_url); } catch (err) { console.warn('suppressed error:', err); }
          const reason = data.email_error ? `Email delivery failed: ${data.email_error}` : 'Email delivery is not configured.';
          toast.warning(`CSA "${newCSA.name}" created. ${reason} Setup link copied to clipboard — share it manually.`, { duration: 15000 });
        }
      } else {
        toast.success(`CSA "${newCSA.name}" created`);
      }

      setNewCSA({ password: '', name: '', email: '', phone: '' });
      setCreateCSAOpen(false);
      loadDashboard();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const handleCreateAdvisor = async () => {
    if (!newAdvisor.name || !newAdvisor.email) {
      toast.error('Name and email are required');
      return;
    }
    if (newAdvisor.password && newAdvisor.password.length < 8) {
      toast.error('Password must be at least 8 characters (or leave blank to send a setup link)');
      return;
    }
    try {
      const payload = { ...newAdvisor };
      if (!payload.username) delete payload.username;
      if (!payload.password) delete payload.password;
      const { data } = await axios.post(`${API}/admin/advisors`, payload, { withCredentials: true });

      if (data.setup_url) {
        // Password-less flow: setup link was generated
        if (data.email_sent) {
          toast.success(`Advisor "${newAdvisor.name}" created and setup link emailed to ${newAdvisor.email}`);
        } else {
          try { await navigator.clipboard.writeText(data.setup_url); } catch (err) { console.warn("suppressed error:", err); }
          const reason = data.email_error ? `Email delivery failed: ${data.email_error}` : 'Email delivery is not configured.';
          toast.warning(`Advisor "${newAdvisor.name}" created. ${reason} Setup link copied to clipboard - share it manually.`, { duration: 15000 });
        }
      } else {
        // Admin supplied an initial password directly
        toast.success(`Advisor "${newAdvisor.name}" created`);
      }

      setNewAdvisor({ username: '', password: '', name: '', email: '', phone: '' });
      setCreateAdvisorOpen(false);
      loadDashboard();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const handleDeleteAdvisor = async (advisorId, name) => {
    if (!window.confirm(`Delete advisor "${name}"? Their invites/submissions remain but become unowned.`)) return;
    try {
      await axios.delete(`${API}/admin/advisors/${advisorId}`, { withCredentials: true });
      toast.success('Advisor deleted');
      loadDashboard();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const handleRoleChange = async (adv, newRole) => {
    if (newRole === adv.role) return;
    if (!window.confirm(`Change ${adv.name || adv.email}'s role from ${adv.role?.toUpperCase()} to ${newRole.toUpperCase()}?`)) return;
    try {
      await axios.patch(`${API}/admin/advisors/${adv.id}`, { role: newRole }, { withCredentials: true });
      toast.success(`${adv.name || adv.email} is now ${newRole.toUpperCase()}`);
      loadDashboard();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  /* -----------------------------------------------------------------------
   * Recipient classification for the Role column on All Invites.
   * Persisted server-side on invite.recipient_type so every admin sees the
   * same tag. Advisor-sent invites are fixed as "Client" (per product rule).
   * --------------------------------------------------------------------- */
  const inviteRecipient = (inv) => {
    const owner = data.advisors.find((a) => a.id === inv.advisor_id);
    const ownerRole = owner?.role || 'advisor';
    if (ownerRole !== 'admin') return { fixed: true, tag: 'client' };
    return { fixed: false, tag: inv.recipient_type || 'client' };
  };
  const setInviteTag = async (inviteId, tag) => {
    // Optimistic UI update
    setData((prev) => ({
      ...prev,
      invites: prev.invites.map((i) => (i.id === inviteId ? { ...i, recipient_type: tag } : i)),
    }));
    try {
      await axios.patch(`${API}/admin/invites/${inviteId}/recipient-type`, { recipient_type: tag }, { withCredentials: true });
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || 'Failed to save tag');
      loadDashboard();
    }
  };
  const RECIPIENT_STYLE = {
    client:  'bg-blue-100 text-blue-800',
    advisor: 'bg-indigo-100 text-indigo-800',
    csa:     'bg-emerald-100 text-emerald-800',
    admin:   'bg-purple-100 text-purple-800',
  };

  const openInviteLinkDialog = (invite) => {
    setSelectedInvite(invite);
    setRetrievedLink('');
    setLinkDialogOpen(true);
  };

  const retrieveInviteLink = async () => {
    if (!selectedInvite) return;
    setRetrieving(true);
    try {
      const { data: r } = await axios.post(
        `${API}/advisor/invites/${selectedInvite.id}/regenerate`,
        {},
        { withCredentials: true }
      );
      const fullLink = r.invite_url || `${window.location.origin}/f/${r.token}`;
      setRetrievedLink(fullLink);
      try { await navigator.clipboard.writeText(fullLink); } catch (err) { console.warn("suppressed error:", err); }
      toast.success(r.email_sent ? 'Fresh link generated and re-emailed to client' : 'Fresh link generated and copied to clipboard');
      loadDashboard();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setRetrieving(false);
    }
  };

  const handleDeleteInvite = async (invite) => {
    if (!window.confirm(`Permanently delete invite for ${invite.client_email}? Any pending draft will be lost. Submitted forms are preserved.`)) return;
    try {
      await axios.delete(`${API}/advisor/invites/${invite.id}`, { withCredentials: true });
      toast.success('Invite deleted');
      setLinkDialogOpen(false);
      loadDashboard();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate('/advisor/login');
  };

  const statusBadge = (status) => {
    const styles = {
      pending: 'bg-yellow-100 text-yellow-800',
      opened: 'bg-blue-100 text-blue-800',
      submitted: 'bg-green-100 text-green-800',
      expired: 'bg-gray-100 text-gray-800',
      revoked: 'bg-red-100 text-red-800',
    };
    return <span className={`px-2 py-1 text-xs font-medium rounded-full ${styles[status] || ''}`}>{status?.toUpperCase()}</span>;
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#f8fafc]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#5b93d6]"></div>
      </div>
    );
  }

  const StatCard = ({ icon: Icon, label, value, color }) => (
    <div className="bg-white rounded-lg shadow-sm border border-[#e2e8f0] p-6">
      <div className="flex items-center gap-4">
        <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${color}`}>
          <Icon className="w-6 h-6 text-white" />
        </div>
        <div>
          <p className="text-sm text-[#475569]" style={{ fontFamily: 'Manrope, sans-serif' }}>{label}</p>
          <p className="text-2xl font-bold text-[#0f172a]" style={{ fontFamily: 'Outfit, sans-serif' }} data-testid={`stat-${label.toLowerCase().replace(/\s/g, '-')}`}>{value}</p>
        </div>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-[#f8fafc]">
      <header className="sticky top-0 z-50 bg-gradient-to-r from-[#0f2444] via-[#1e3a63] to-[#26518c] shadow-lg">
        <div className="container mx-auto px-4 sm:px-6 py-4 flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-4">
            <img src="/stpk-logo.jpg" alt="SteelPeak" className="w-10 h-10 rounded-md shadow-sm" data-testid="brand-logo" />
            <h1 className="text-lg sm:text-2xl font-bold text-white" style={{ fontFamily: 'Outfit, sans-serif', letterSpacing: '0.05em' }}>
              <span style={{ fontWeight: 800 }}>STEELPEAK</span>{' '}
              <span style={{ fontWeight: 300 }}>WEALTH</span>
              <span className="ml-3 text-sm bg-[#5b93d6] px-2 py-1 rounded font-medium">ADMIN</span>
            </h1>
          </div>
          <div className="flex flex-wrap items-center gap-2 sm:gap-3">
            <AdvisorNotifications />
            <SwitchViewMenu current="admin" roleLabel="ADMIN" />
            <Button
              variant="ghost"
              size="sm"
              onClick={handleRunReminders}
              disabled={runningReminders}
              className="text-white hover:bg-white/10"
              data-testid="run-reminders-button"
              title="Manually run the reminder scanner now"
            >
              <RefreshCw className={`w-4 h-4 mr-2 ${runningReminders ? 'animate-spin' : ''}`} />
              {runningReminders ? 'Scanning…' : 'Send Reminders'}
            </Button>
            <Button variant="ghost" size="sm" onClick={handleLogout} className="text-white hover:bg-white/10" data-testid="logout-button">
              <LogOut className="w-4 h-4 mr-2" />
              Logout
            </Button>
          </div>
        </div>
      </header>

      <div className="container mx-auto px-6 py-8">
        <div className="mb-8">
          <h2 className="text-3xl font-bold text-[#0f172a]" style={{ fontFamily: 'Outfit, sans-serif' }} data-testid="admin-heading">
            Admin Control Center
          </h2>
          <p className="text-[#475569] mt-1" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Manage advisors and monitor all client activity
          </p>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
          <StatCard icon={Users} label="Total Advisors" value={data.stats.total_advisors} color="bg-[#5b93d6]" />
          <StatCard icon={Send} label="Total Invites" value={data.stats.total_invites} color="bg-[#1e3a63]" />
          <StatCard icon={Clock} label="Pending Invites" value={data.stats.pending_invites} color="bg-[#26518c]" />
          <StatCard icon={FileText} label="Total Submissions" value={data.stats.total_submissions} color="bg-[#0f2444]" />
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="grid h-auto w-full grid-cols-2 sm:grid-cols-4 xl:grid-cols-8 gap-1 max-w-5xl mb-6">
            <TabsTrigger value="advisors" data-testid="tab-advisors">Advisors</TabsTrigger>
            <TabsTrigger value="csas" data-testid="tab-csas">CSA</TabsTrigger>
            <TabsTrigger value="allowlist" data-testid="tab-allowlist">Signup Allowlist</TabsTrigger>
            <TabsTrigger value="invites" data-testid="tab-invites">All Invites</TabsTrigger>
            <TabsTrigger value="submissions" data-testid="tab-submissions">All Submissions</TabsTrigger>
            <TabsTrigger value="preview" data-testid="tab-preview">Form Preview</TabsTrigger>
            <TabsTrigger value="analytics" data-testid="tab-analytics">Analytics</TabsTrigger>
            <TabsTrigger value="audit" data-testid="tab-audit">Audit Log</TabsTrigger>
          </TabsList>

          {/* Advisors Tab */}
          <TabsContent value="advisors">
            <div className="mb-4 flex flex-wrap justify-between items-center gap-3">
              <h3 className="text-xl font-bold text-[#0f172a]" style={{ fontFamily: 'Outfit, sans-serif' }}>
                Advisors ({data.advisors.filter(a => a.role !== 'csa').length})
              </h3>
              <Dialog open={createAdvisorOpen} onOpenChange={setCreateAdvisorOpen}>
                <DialogTrigger asChild>
                  <Button className="bg-[#5b93d6] hover:bg-[#4a7dc5] text-white" data-testid="create-advisor-button">
                    <UserPlus className="w-4 h-4 mr-2" />
                    Add Advisor
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>Add New Advisor</DialogTitle>
                    <DialogDescription>
                      Create an advisor account and optionally send a "set your password" link.
                    </DialogDescription>
                  </DialogHeader>
                  <div className="space-y-4 py-4">
                    <div>
                      <Label htmlFor="adv-username">Username (optional)</Label>
                      <Input id="adv-username" value={newAdvisor.username} onChange={(e) => setNewAdvisor({ ...newAdvisor, username: e.target.value })} placeholder="auto-derived from email if blank" className="mt-2" data-testid="advisor-username-input" />
                    </div>
                    <div>
                      <Label htmlFor="adv-password">Password <span className="text-[#64748b] font-normal">(optional)</span></Label>
                      <Input id="adv-password" type="password" value={newAdvisor.password} onChange={(e) => setNewAdvisor({ ...newAdvisor, password: e.target.value })} placeholder="Leave blank to email a setup link" className="mt-2" data-testid="advisor-password-input" />
                      <p className="text-xs text-[#64748b] mt-1.5">
                        Leave blank and we'll email the advisor a "set your password" link (recommended — advisor picks their own).
                      </p>
                    </div>
                    <div>
                      <Label htmlFor="adv-name">Full Name *</Label>
                      <Input id="adv-name" value={newAdvisor.name} onChange={(e) => setNewAdvisor({ ...newAdvisor, name: e.target.value })} placeholder="John Smith" className="mt-2" data-testid="advisor-name-input" />
                    </div>
                    <div>
                      <Label htmlFor="adv-email">Email *</Label>
                      <Input id="adv-email" type="email" value={newAdvisor.email} onChange={(e) => setNewAdvisor({ ...newAdvisor, email: e.target.value })} placeholder="john@steelpeak.com" className="mt-2" data-testid="advisor-email-input" />
                    </div>
                    <div>
                      <Label htmlFor="adv-phone">Phone</Label>
                      <Input id="adv-phone" value={newAdvisor.phone} onChange={(e) => setNewAdvisor({ ...newAdvisor, phone: e.target.value })} placeholder="(555) 123-4567" className="mt-2" data-testid="advisor-phone-input" />
                    </div>
                    <Button onClick={handleCreateAdvisor} className="w-full bg-gradient-to-r from-[#0f2444] via-[#1e3a63] to-[#26518c] hover:opacity-90" data-testid="submit-advisor-button">
                      Create Advisor
                    </Button>
                  </div>
                </DialogContent>
              </Dialog>
            </div>

            <div className="bg-white rounded-lg shadow-sm border border-[#e2e8f0] overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full portal-table">
                  <thead className="bg-[#f8fafc] border-b border-[#e2e8f0]">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-[#475569] uppercase tracking-wider">Username</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-[#475569] uppercase tracking-wider">Name</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-[#475569] uppercase tracking-wider">Email</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-[#475569] uppercase tracking-wider">Phone</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-[#475569] uppercase tracking-wider">Role</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-[#475569] uppercase tracking-wider">Invites</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-[#475569] uppercase tracking-wider">Submissions</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-[#475569] uppercase tracking-wider">Last Active</th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-[#475569] uppercase tracking-wider">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-[#e2e8f0]">
                    {data.advisors.filter((adv) => adv.role !== 'csa').map((adv) => (
                      <tr key={adv.id} className="hover:bg-[#f8fafc]" data-testid={`advisor-row-${adv.id}`}>
                        <td className="px-6 py-4 text-sm text-[#0f172a] font-medium">{adv.username || adv.email}</td>
                        <td className="px-6 py-4 text-sm text-[#0f172a]" data-label="Name">
                          {adv.name}
                          <p className="mt-1 text-xs text-[#475569]" data-testid={`advisor-setup-status-${adv.id}`}>
                            {adv.is_active === false ? 'Inactive' : adv.must_set_password ? 'Awaiting setup' : 'Setup complete'}
                          </p>
                        </td>
                        <td className="px-6 py-4 text-sm text-[#475569]" data-label="Email">{adv.email}</td>
                        <td className="px-6 py-4 text-sm text-[#475569]" data-label="Phone">{adv.phone || '—'}</td>
                        <td className="px-6 py-4">
                          <select
                            value={adv.role || 'advisor'}
                            onChange={(e) => handleRoleChange(adv, e.target.value)}
                            disabled={adv.id === user?.id}
                            className={`text-xs font-medium rounded-full px-2 py-1 border-0 cursor-pointer disabled:cursor-not-allowed disabled:opacity-70 ${
                              adv.role === 'admin' ? 'bg-purple-100 text-purple-800'
                                : adv.role === 'csa' ? 'bg-emerald-100 text-emerald-800'
                                : 'bg-blue-100 text-blue-800'
                            }`}
                            data-testid={`role-select-${adv.id}`}
                            title={adv.id === user?.id ? "You can't change your own role" : 'Change role'}
                          >
                            <option value="advisor">ADVISOR</option>
                            <option value="admin">ADMIN</option>
                            <option value="csa">CSA</option>
                          </select>
                        </td>
                        <td className="px-6 py-4 text-sm text-[#475569]" data-label="Invites">{adv.invite_count ?? 0}</td>
                        <td className="px-6 py-4 text-sm text-[#475569]" data-label="Submissions">{adv.submission_count ?? 0}</td>
                        <td className="px-6 py-4 text-sm text-[#475569]" data-label="Last active" data-testid={`advisor-last-active-${adv.id}`}>{formatLastActive(adv.last_active_at)}</td>
                        <td className="px-6 py-4 text-right">
                          <div className="flex justify-end gap-1">
                            {adv.must_set_password && adv.is_active !== false && adv.role !== 'admin' && (
                              <ResendInviteButton staff endpoint={`/admin/advisors/${adv.id}/resend-invite`}
                                recipient={adv.email} onResent={loadDashboard} testId={`resend-advisor-invite-${adv.id}`} />
                            )}
                            <Button variant="ghost" size="sm" onClick={() => openEditDialog(adv)} data-testid={`edit-advisor-${adv.id}`}>
                              <Pencil className="w-4 h-4" />
                            </Button>
                            {adv.role !== 'admin' && (
                              <Button variant="ghost" size="sm" onClick={() => handleDeleteAdvisor(adv.id, adv.name)} data-testid={`delete-advisor-${adv.id}`}>
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </TabsContent>

          {/* CSA Tab */}
          <TabsContent value="csas">
            <div className="mb-4 flex flex-wrap justify-between items-center gap-3">
              <h3 className="text-xl font-bold text-[#0f172a]" style={{ fontFamily: 'Outfit, sans-serif' }}>
                Client Service Associates ({data.advisors.filter(a => a.role === 'csa').length})
              </h3>
              <Dialog open={createCSAOpen} onOpenChange={setCreateCSAOpen}>
                <DialogTrigger asChild>
                  <Button className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="create-csa-button">
                    <UserPlus className="w-4 h-4 mr-2" />
                    Add CSA
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>Invite a Client Service Associate</DialogTitle>
                    <DialogDescription>
                      CSAs can view and download submitted Client Profiles across the firm, and resend client invitations. Submitted profiles remain read-only.
                    </DialogDescription>
                  </DialogHeader>
                  <div className="space-y-4 py-4">
                    <div>
                      <Label htmlFor="csa-name">Full Name *</Label>
                      <Input id="csa-name" value={newCSA.name} onChange={(e) => setNewCSA({ ...newCSA, name: e.target.value })} placeholder="Jane Smith" className="mt-2" data-testid="csa-name-input" />
                    </div>
                    <div>
                      <Label htmlFor="csa-email">Email *</Label>
                      <Input id="csa-email" type="email" value={newCSA.email} onChange={(e) => setNewCSA({ ...newCSA, email: e.target.value })} placeholder="jane@spwm.com" className="mt-2" data-testid="csa-email-input" />
                    </div>
                    <div>
                      <Label htmlFor="csa-phone">Phone</Label>
                      <Input id="csa-phone" value={newCSA.phone} onChange={(e) => setNewCSA({ ...newCSA, phone: e.target.value })} placeholder="(555) 123-4567" className="mt-2" data-testid="csa-phone-input" />
                    </div>
                    <div>
                      <Label htmlFor="csa-password">Password <span className="text-[#64748b] font-normal">(optional)</span></Label>
                      <Input id="csa-password" type="password" value={newCSA.password} onChange={(e) => setNewCSA({ ...newCSA, password: e.target.value })} placeholder="Leave blank to email a setup link" className="mt-2" data-testid="csa-password-input" />
                      <p className="text-xs text-[#64748b] mt-1.5">
                        Leave blank and we'll email the CSA a "set your password" link (recommended).
                      </p>
                    </div>
                    <Button onClick={handleCreateCSA} className="w-full bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="submit-csa-button">
                      Invite CSA
                    </Button>
                  </div>
                </DialogContent>
              </Dialog>
            </div>

            <div className="bg-white rounded-lg shadow-sm border border-[#e2e8f0] overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full portal-table">
                  <thead className="bg-[#f8fafc] border-b border-[#e2e8f0]">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-[#475569] uppercase tracking-wider">Name</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-[#475569] uppercase tracking-wider">Email</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-[#475569] uppercase tracking-wider">Phone</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-[#475569] uppercase tracking-wider">Role</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-[#475569] uppercase tracking-wider">Last Active</th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-[#475569] uppercase tracking-wider">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-[#e2e8f0]">
                    {data.advisors.filter((adv) => adv.role === 'csa').length === 0 ? (
                      <tr><td colSpan="6" className="px-6 py-8 text-center text-[#475569]">No CSAs yet. Invite your first Client Service Associate to give them read-only visibility of all client submissions.</td></tr>
                    ) : (
                      data.advisors.filter((adv) => adv.role === 'csa').map((adv) => (
                        <tr key={adv.id} className="hover:bg-[#f8fafc]" data-testid={`csa-row-${adv.id}`}>
                          <td className="px-6 py-4 text-sm text-[#0f172a] font-medium" data-label="Name">
                            {adv.name}
                            <p className="mt-1 text-xs font-normal text-[#475569]" data-testid={`csa-setup-status-${adv.id}`}>
                              {adv.is_active === false ? 'Inactive' : adv.must_set_password ? 'Awaiting setup' : 'Setup complete'}
                            </p>
                          </td>
                          <td className="px-6 py-4 text-sm text-[#475569]" data-label="Email">{adv.email}</td>
                          <td className="px-6 py-4 text-sm text-[#475569]" data-label="Phone">{adv.phone || '—'}</td>
                          <td className="px-6 py-4">
                            <select
                              value={adv.role || 'csa'}
                              onChange={(e) => handleRoleChange(adv, e.target.value)}
                              disabled={adv.id === user?.id}
                              className="text-xs font-medium rounded-full px-2 py-1 border-0 cursor-pointer disabled:cursor-not-allowed disabled:opacity-70 bg-emerald-100 text-emerald-800"
                              data-testid={`csa-role-select-${adv.id}`}
                              title={adv.id === user?.id ? "You can't change your own role" : 'Change role'}
                            >
                              <option value="advisor">ADVISOR</option>
                              <option value="admin">ADMIN</option>
                              <option value="csa">CSA</option>
                            </select>
                          </td>
                          <td className="px-6 py-4 text-sm text-[#475569]">{formatLastActive(adv.last_active_at)}</td>
                          <td className="px-6 py-4 text-right">
                            <div className="flex justify-end gap-1">
                              {adv.must_set_password && adv.is_active !== false && (
                                <ResendInviteButton staff endpoint={`/admin/advisors/${adv.id}/resend-invite`}
                                  recipient={adv.email} onResent={loadDashboard} testId={`resend-csa-invite-${adv.id}`} />
                              )}
                              <Button variant="ghost" size="sm" onClick={() => openEditDialog(adv)} data-testid={`edit-csa-${adv.id}`}>
                                <Pencil className="w-4 h-4" />
                              </Button>
                              <Button variant="ghost" size="sm" onClick={() => handleDeleteAdvisor(adv.id, adv.name)} data-testid={`delete-csa-${adv.id}`}>
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </TabsContent>

          {/* Signup Allowlist Tab */}
          <TabsContent value="allowlist">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-bold text-[#0f172a]" style={{ fontFamily: 'Outfit, sans-serif' }}>
                Signup Allowlist ({allowlist.length})
              </h3>
            </div>
            <p className="text-sm text-[#475569] mb-4">
              Only emails on this list can self-register at <code className="bg-[#f1f5f9] px-1.5 py-0.5 rounded">/advisor/signup</code>.
              Each entry is one-time use.
            </p>

            <div className="bg-white rounded-lg shadow-sm border border-[#e2e8f0] p-6 mb-6">
              <Label htmlFor="new-allow-email" className="text-[#0f172a]">Add Email to Allowlist</Label>
              <div className="flex flex-col sm:flex-row gap-3 mt-2">
                <Input
                  id="new-allow-email"
                  type="email"
                  placeholder="new-user@example.com"
                  value={newAllowEmail}
                  onChange={(e) => setNewAllowEmail(e.target.value)}
                  className="flex-1"
                  data-testid="allowlist-email-input"
                />
                <select
                  value={newAllowRole}
                  onChange={(e) => setNewAllowRole(e.target.value)}
                  className="rounded-md border border-[#e2e8f0] bg-white px-3 py-2 text-sm text-[#0f172a] shadow-sm sm:w-40"
                  data-testid="allowlist-role-select"
                >
                  <option value="advisor">Advisor</option>
                  <option value="admin">Admin</option>
                  <option value="csa">CSA</option>
                </select>
                <Button
                  onClick={handleAddAllowlist}
                  className="bg-gradient-to-r from-[#0f2444] via-[#1e3a63] to-[#26518c] hover:opacity-90 text-white"
                  data-testid="allowlist-add-button"
                >
                  <UserPlus className="w-4 h-4 mr-2" /> Add
                </Button>
              </div>
              <p className="text-xs text-[#475569] mt-2">
                Advisors send invites and view their own submissions. Admins manage everyone. CSAs get read-only visibility of all submissions across the firm.
              </p>
            </div>

            <div className="bg-white rounded-lg shadow-sm border border-[#e2e8f0] overflow-hidden" data-testid="allowlist-table">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-[#f8fafc]">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-[#475569] uppercase">Email</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-[#475569] uppercase">Role</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-[#475569] uppercase">Status</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-[#475569] uppercase">Added</th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-[#475569] uppercase">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-[#e2e8f0]">
                    {allowlist.length === 0 ? (
                      <tr>
                        <td colSpan={5} className="px-6 py-12 text-center text-[#475569]" data-testid="allowlist-empty">
                          No allowlisted emails yet. Add one above to let a new advisor self-signup.
                        </td>
                      </tr>
                    ) : (
                      allowlist.map((entry) => (
                        <tr key={entry.email} data-testid={`allowlist-row-${entry.email}`}>
                          <td className="px-6 py-4 text-sm text-[#0f172a]" data-testid={`allowlist-email-cell-${entry.email}`}>{entry.email}</td>
                          <td className="px-6 py-4 text-sm text-[#475569] capitalize">{entry.role || 'advisor'}</td>
                          <td className="px-6 py-4 text-sm">
                            {entry.used ? (
                              <span className="px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800" data-testid={`allowlist-status-${entry.email}`}>Used</span>
                            ) : (
                              <span className="px-2 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800" data-testid={`allowlist-status-${entry.email}`}>Pending</span>
                            )}
                          </td>
                          <td className="px-6 py-4 text-sm text-[#475569]">
                            {entry.created_at ? new Date(entry.created_at).toLocaleDateString() : '—'}
                          </td>
                          <td className="px-6 py-4 text-right">
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => handleRemoveAllowlist(entry.email)}
                              data-testid={`allowlist-remove-${entry.email}`}
                            >
                              <Trash2 className="w-4 h-4 text-red-600" />
                            </Button>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </TabsContent>


          {/* All Invites Tab */}
          <TabsContent value="invites">
            <h3 className="text-xl font-bold text-[#0f172a] mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
              All Client Invites ({data.invites.length})
            </h3>
            <div className="bg-white rounded-lg shadow-sm border border-[#e2e8f0] overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-[#f8fafc] border-b border-[#e2e8f0]">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-[#475569] uppercase">Role</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-[#475569] uppercase">Client</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-[#475569] uppercase">Email</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-[#475569] uppercase">Status</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-[#475569] uppercase">Created</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-[#475569] uppercase">Expires</th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-[#475569] uppercase">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-[#e2e8f0]">
                    {data.invites.length === 0 ? (
                      <tr><td colSpan="7" className="px-6 py-8 text-center text-[#475569]">No invites yet.</td></tr>
                    ) : (
                      data.invites.map((inv) => (
                        <tr key={inv.id} className="hover:bg-[#f8fafc]" data-testid={`admin-invite-${inv.id}`}>
                          <td className="px-6 py-4">
                            {(() => {
                              const { fixed, tag } = inviteRecipient(inv);
                              const cls = RECIPIENT_STYLE[tag] || RECIPIENT_STYLE.client;
                              const owner = data.advisors.find((a) => a.id === inv.advisor_id);
                              const senderLabel = owner ? `Sent by ${owner.name || owner.email}` : '';
                              if (fixed) {
                                return (
                                  <div className="flex flex-col gap-1">
                                    <span className={`inline-flex self-start text-xs font-medium rounded-full px-2 py-1 ${cls}`} data-testid={`invite-recipient-${inv.id}`}>CLIENT</span>
                                    <span className="text-xs text-[#475569]">{senderLabel}</span>
                                  </div>
                                );
                              }
                              return (
                                <div className="flex flex-col gap-1">
                                  <select
                                    value={tag}
                                    onChange={(e) => setInviteTag(inv.id, e.target.value)}
                                    className={`text-xs font-medium rounded-full px-2 py-1 border-0 cursor-pointer ${cls}`}
                                    data-testid={`invite-recipient-select-${inv.id}`}
                                    title="Tag who this invite was sent to"
                                  >
                                    <option value="client">CLIENT</option>
                                    <option value="advisor">ADVISOR</option>
                                    <option value="csa">CSA</option>
                                    <option value="admin">ADMIN</option>
                                  </select>
                                  <span className="text-xs text-[#475569]">{senderLabel}</span>
                                </div>
                              );
                            })()}
                          </td>
                          <td className="px-6 py-4 text-sm">
                            <button
                              onClick={() => openInviteLinkDialog(inv)}
                              className="text-[#0f172a] hover:text-[#5b93d6] hover:underline text-left cursor-pointer"
                              data-testid={`admin-invite-name-link-${inv.id}`}
                              title="Click to retrieve invite link"
                            >
                              {inv.client_name || inv.client_email}
                            </button>
                          </td>
                          <td className="px-6 py-4 text-sm text-[#475569]">{inv.client_email}</td>
                          <td className="px-6 py-4">{statusBadge(inv.status)}</td>
                          <td className="px-6 py-4 text-sm text-[#475569]">{new Date(inv.created_at).toLocaleDateString()}</td>
                          <td className="px-6 py-4 text-sm text-[#475569]">{new Date(inv.expires_at).toLocaleDateString()}</td>
                          <td className="px-6 py-4 text-right">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleDeleteInvite(inv)}
                              data-testid={`admin-delete-invite-${inv.id}`}
                              title="Delete invite"
                            >
                              <Trash2 className="w-4 h-4 text-red-600" />
                            </Button>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </TabsContent>

          {/* All Submissions Tab */}
          <TabsContent value="submissions">
            <h3 className="text-xl font-bold text-[#0f172a] mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
              All Submissions ({data.submissions.length})
            </h3>
            <div className="bg-white rounded-lg shadow-sm border border-[#e2e8f0] overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-[#f8fafc] border-b border-[#e2e8f0]">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-[#475569] uppercase">Role</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-[#475569] uppercase">Client</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-[#475569] uppercase">Email</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-[#475569] uppercase">Submitted</th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-[#475569] uppercase">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-[#e2e8f0]">
                    {data.submissions.length === 0 ? (
                      <tr><td colSpan="5" className="px-6 py-8 text-center text-[#475569]">No submissions yet.</td></tr>
                    ) : (
                      data.submissions.map((sub) => (
                        <tr key={sub.id} className="hover:bg-[#f8fafc]" data-testid={`admin-submission-${sub.id}`}>
                          <td className="px-6 py-4">
                            <div className="flex flex-col gap-1">
                              <span className="inline-flex self-start text-xs font-medium rounded-full px-2 py-1 bg-blue-100 text-blue-800" data-testid={`submission-recipient-${sub.id}`}>CLIENT</span>
                              <span className="text-xs text-[#475569]">Advisor: {sub.advisor_name}</span>
                            </div>
                          </td>
                          <td className="px-6 py-4 text-sm text-[#0f172a]">{sub.client_name || '—'}</td>
                          <td className="px-6 py-4 text-sm text-[#475569]">{sub.client_email}</td>
                          <td className="px-6 py-4 text-sm text-[#475569]">{new Date(sub.submitted_at).toLocaleDateString()}</td>
                          <td className="px-6 py-4 text-right">
                            <Button variant="ghost" size="sm" onClick={() => navigate(`/advisor/submission/${sub.id}`)} data-testid={`view-admin-submission-${sub.id}`}>
                              <Eye className="w-4 h-4 mr-2" />View
                            </Button>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </TabsContent>

          {/* Form Preview Tab */}
          <TabsContent value="preview">
            <FormPreview />
          </TabsContent>

          {/* Analytics Tab */}
          <TabsContent value="analytics">
            <AnalyticsPanel
              analytics={analytics}
              onAdvisorClick={(row) => navigate(`/admin/advisors/${row.advisor_id}`)}
            />
          </TabsContent>

          {/* Audit Log Tab */}
          <TabsContent value="audit">
            <h3 className="text-xl font-bold text-[#0f172a] mb-4 flex items-center gap-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
              <Activity className="w-5 h-5 text-[#5b93d6]" /> Audit Log ({auditLogs.length})
            </h3>
            <div className="bg-white rounded-lg shadow-sm border border-[#e2e8f0] overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-[#f8fafc] border-b border-[#e2e8f0]">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-[#475569] uppercase">Timestamp</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-[#475569] uppercase">Actor</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-[#475569] uppercase">Action</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-[#475569] uppercase">Details</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-[#e2e8f0]">
                    {auditLogs.length === 0 ? (
                      <tr><td colSpan="4" className="px-6 py-8 text-center text-[#475569]">No admin actions logged yet.</td></tr>
                    ) : (
                      auditLogs.map((log) => (
                        <tr key={log.id} className="hover:bg-[#f8fafc]" data-testid={`audit-row-${log.id}`}>
                          <td className="px-6 py-4 text-sm text-[#475569] whitespace-nowrap">{new Date(log.created_at).toLocaleString()}</td>
                          <td className="px-6 py-4 text-sm text-[#0f172a] font-medium">
                            {log.actor_name}
                            <div className="text-xs text-[#475569]">@{log.actor_username}</div>
                          </td>
                          <td className="px-6 py-4">
                            <span className="px-2 py-1 text-xs font-medium rounded-full bg-slate-100 text-slate-800 font-mono">{log.action}</span>
                          </td>
                          <td className="px-6 py-4 text-sm text-[#475569] font-mono text-xs">
                            {Object.entries(log.details || {}).filter(([k, v]) => v != null && v !== '').map(([k, v]) => (
                              <div key={k}><span className="text-[#94a3b8]">{k}:</span> {Array.isArray(v) ? v.join(', ') : String(v)}</div>
                            ))}
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </TabsContent>
        </Tabs>

        {/* Retrieve Invite Link Dialog */}
        <Dialog open={linkDialogOpen} onOpenChange={setLinkDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Invite Link — {selectedInvite?.client_name || selectedInvite?.client_email}</DialogTitle>
              <DialogDescription>
                Retrieve a fresh invite link, or delete this invite entirely.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">
                For security, invite tokens are stored one-way and cannot be recovered. Click below to generate a
                <strong> fresh </strong> link (the previous link will stop working) and email it to the client.
              </div>
              <div className="text-sm text-[#475569] space-y-1">
                <div><strong>Advisor:</strong> {selectedInvite?.advisor_name}</div>
                <div><strong>Client:</strong> {selectedInvite?.client_name || '—'}</div>
                <div><strong>Email:</strong> {selectedInvite?.client_email}</div>
                <div><strong>Status:</strong> {selectedInvite?.status?.toUpperCase()}</div>
                <div><strong>Expires:</strong> {selectedInvite ? new Date(selectedInvite.expires_at).toLocaleDateString() : '—'}</div>
              </div>

              {!retrievedLink ? (
                <Button
                  onClick={retrieveInviteLink}
                  disabled={retrieving || selectedInvite?.status === 'submitted' || selectedInvite?.status === 'revoked'}
                  className="w-full bg-gradient-to-r from-[#0f2444] via-[#1e3a63] to-[#26518c] hover:opacity-90"
                  data-testid="admin-retrieve-link-button"
                >
                  <RefreshCw className={`w-4 h-4 mr-2 ${retrieving ? 'animate-spin' : ''}`} />
                  {retrieving ? 'Generating…' : 'Generate & Copy Fresh Link'}
                </Button>
              ) : (
                <div className="rounded-md border border-green-200 bg-green-50 p-3">
                  <p className="text-sm font-medium text-green-900 mb-2">Fresh link ready (copied to clipboard):</p>
                  <div className="flex gap-2">
                    <Input value={retrievedLink} readOnly className="text-sm" data-testid="admin-retrieved-link-input" />
                    <Button onClick={() => { navigator.clipboard.writeText(retrievedLink); toast.success('Copied'); }} size="sm" data-testid="admin-copy-retrieved-link-button">
                      <Copy className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              )}

              <Button
                variant="outline"
                onClick={() => handleDeleteInvite(selectedInvite)}
                className="w-full text-red-600 hover:text-red-700 border-red-200"
                data-testid="admin-dialog-delete-invite-button"
              >
                <Trash2 className="w-4 h-4 mr-2" /> Delete this invite
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* Edit Advisor Dialog */}
        <Dialog open={editAdvisorOpen} onOpenChange={setEditAdvisorOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Edit Advisor</DialogTitle>
              <DialogDescription>
                Update advisor details or reset their password.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div>
                <Label htmlFor="edit-name">Full Name</Label>
                <Input id="edit-name" value={editAdvisor.name} onChange={(e) => setEditAdvisor({ ...editAdvisor, name: e.target.value })} className="mt-2" data-testid="edit-name-input" />
              </div>
              <div>
                <Label htmlFor="edit-email">Email</Label>
                <Input id="edit-email" type="email" value={editAdvisor.email} onChange={(e) => setEditAdvisor({ ...editAdvisor, email: e.target.value })} className="mt-2" data-testid="edit-email-input" />
              </div>
              <div>
                <Label htmlFor="edit-phone">Phone</Label>
                <Input id="edit-phone" value={editAdvisor.phone} onChange={(e) => setEditAdvisor({ ...editAdvisor, phone: e.target.value })} className="mt-2" data-testid="edit-phone-input" />
              </div>
              <div>
                <Label htmlFor="edit-password">New Password <span className="text-xs text-[#475569]">(leave blank to keep current)</span></Label>
                <Input id="edit-password" type="password" value={editAdvisor.password} onChange={(e) => setEditAdvisor({ ...editAdvisor, password: e.target.value })} placeholder="••••••••" className="mt-2" data-testid="edit-password-input" />
              </div>
              <Button onClick={handleUpdateAdvisor} className="w-full bg-gradient-to-r from-[#0f2444] via-[#1e3a63] to-[#26518c] hover:opacity-90" data-testid="save-advisor-button">
                Save Changes
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
}
